import axios from "axios";
import Cookies from "js-cookie";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Attach the session token to every request we send to our own backend.
 *
 * Every API module imports this file for BASE_URL, so registering the
 * interceptor here covers all call sites without touching each one. Note the
 * existing calls pass `Headers:` (capital H), which axios ignores - so before
 * this, no request carried an Authorization header at all.
 */
axios.interceptors.request.use((config) => {
  const url = config.url || "";

  // Only our own API. Google's userinfo endpoint is called with its own
  // Authorization header and must not receive our session token.
  if (!BASE_URL || !url.startsWith(BASE_URL)) return config;

  // Never overwrite an Authorization header a caller set deliberately.
  if (config.headers?.Authorization) return config;

  try {
    const raw = Cookies.get("userKeys");
    const token = raw ? JSON.parse(raw)?.userToken : null;
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Malformed cookie - send the request unauthenticated and let the API
    // reject it, rather than breaking every call in the app.
  }

  return config;
});

/**
 * End the session when the API says it is over.
 *
 * Sessions now expire (12 hours, or 30 days when remembered), and are revoked
 * at once by a ban or a password change (QA finding SEC-06). Without this, the
 * app would stay on screen while every request quietly failed. A 401 - or a
 * 403 flagged as a restricted account - clears the stored session and returns
 * to the sign-in page.
 *
 * Sign-in requests are left alone so the screens can show their own messages
 * ("Invalid credentials", "not approved") instead of being redirected.
 */
const SIGN_IN_PATHS = ["/sign-in", "/auth/google", "/Signup", "/signInWithGoogle", "/emailVerify"];

const endSession = () => {
  Cookies.remove("userKeys");
  Cookies.remove("currentUser");
  // A full navigation also clears the in-memory user store.
  if (window.location.pathname !== "/") window.location.assign("/");
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || "";
    const status = error?.response?.status;
    const code = error?.response?.data?.code;

    const ours = BASE_URL && url.startsWith(BASE_URL);
    const signingIn = SIGN_IN_PATHS.some((path) => url.startsWith(BASE_URL + path));
    const sessionOver =
      status === 401 || (status === 403 && code === "ACCOUNT_RESTRICTED");

    if (ours && !signingIn && sessionOver) endSession();
    return Promise.reject(error);
  }
);

export default BASE_URL;
